import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, ChevronRight, Loader2, MoreHorizontal, Plus, Repeat } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission } from "../../lib/permissions";
import { todayIso } from "../../lib/finance";
import {
  OBLIGATION_KINDS, OBLIGATION_STATE_LABELS, RECURRENCES, canWrite, dueText, newKey, obligationSuggestions, obligationTone, recordsError,
} from "../../lib/records";
import TaskFormSheet from "../TaskFormSheet";
import { usePeople } from "./RecordFields";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../ui/responsive-dropdown-menu";

export function ObligationBadge({ state }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${obligationTone(state)}`}
      data-testid={`obligation-state-${state}`}>
      {OBLIGATION_STATE_LABELS[state] || state}
    </span>
  );
}

function Row({ id, label, children }) {
  return <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label>{children}</div>;
}

function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (request, onDone, success) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await request();
      toast.success(success);
      onDone(data);
    } catch (e) {
      setError(recordsError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

function Footer({ req, onClose, onSubmit, disabled, label, danger, testid }) {
  return (
    <>
      {req.error && <p className="text-sm text-red-600" role="alert" data-testid="obligation-dialog-error">{req.error}</p>}
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={req.busy}>Fermer</Button>
        <Button variant={danger ? "destructive" : "default"} onClick={onSubmit} disabled={req.busy || disabled} data-testid={testid}>
          {req.busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{label}
        </Button>
      </DialogFooter>
    </>
  );
}

const blank = (user, preset) => ({
  title: "", kind: "other", due_date: "", recurrence: "none", responsible_user_id: user?.id || "", note: "", ...(preset || {}),
});

// Create (optionally prefilled, e.g. from a document's dates — D-40) or edit an open obligation.
export function ObligationDialog({ open, onClose, onDone, obligation, preset, document }) {
  const { user } = useAuth();
  const people = usePeople();
  const req = useSubmit();
  const [form, setForm] = useState(() => blank(user, preset));
  const [key, setKey] = useState(newKey);
  const set = (field) => (value) => setForm((f) => ({ ...f, [field]: value }));
  useEffect(() => {
    if (!open) return;
    setForm(obligation ? { title: obligation.title, kind: obligation.kind, due_date: obligation.due_date, recurrence: obligation.recurrence,
      responsible_user_id: obligation.responsible_user_id, note: obligation.note || "" } : blank(user, preset));
    setKey(newKey());
    req.setError("");
  }, [open, obligation, preset]); // eslint-disable-line react-hooks/exhaustive-deps
  const classification = document?.classification || "internal";
  const options = people.filter((p) => classification !== "confidential" || p.confidential);
  const valid = form.title.trim().length >= 2 && /^\d{4}-\d{2}-\d{2}$/.test(form.due_date) && form.responsible_user_id;
  const submit = () => {
    const body = { title: form.title.trim(), kind: form.kind, due_date: form.due_date, recurrence: form.recurrence,
      responsible_user_id: form.responsible_user_id, note: form.note.trim() };
    if (obligation) {
      req.run(() => api.patch(`/records/obligations/${obligation.id}`, { expected_version: obligation.version, ...body }), onDone, "Obligation modifiée.");
    } else {
      req.run(() => api.post("/records/obligations", { ...body, document_id: document?.id || null }, { headers: { "Idempotency-Key": key } }),
        onDone, "Obligation créée.");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{obligation ? "Modifier l'obligation" : "Nouvelle obligation"}</DialogTitle>
          <DialogDescription>{document ? `Liée au document ${document.number}.` : "Quelque chose à faire avant une date : une alerte prévient le responsable."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Row id="obligation-title" label="Titre">
            <Input id="obligation-title" value={form.title} maxLength={200} onChange={(e) => set("title")(e.target.value)} className="h-10" data-testid="obligation-title" />
          </Row>
          <div className="grid gap-4 sm:grid-cols-2">
            <Row id="obligation-kind" label="Nature">
              <Select value={form.kind} onValueChange={set("kind")}>
                <SelectTrigger id="obligation-kind" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(OBLIGATION_KINDS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row id="obligation-due" label="Date limite">
              <Input id="obligation-due" type="date" value={form.due_date} onChange={(e) => set("due_date")(e.target.value)} className="h-10" data-testid="obligation-due" />
            </Row>
            <Row id="obligation-recurrence" label="Récurrence">
              <Select value={form.recurrence} onValueChange={set("recurrence")}>
                <SelectTrigger id="obligation-recurrence" className="h-10" data-testid="obligation-recurrence"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(RECURRENCES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row id="obligation-responsible" label="Responsable">
              <Select value={form.responsible_user_id} onValueChange={set("responsible_user_id")}>
                <SelectTrigger id="obligation-responsible" className="h-10" data-testid="obligation-responsible"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>
                  {obligation && !options.some((p) => p.id === obligation.responsible_user_id) && (
                    <SelectItem value={obligation.responsible_user_id}>{obligation.responsible_name}</SelectItem>
                  )}
                  {options.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Row>
          </div>
          <Row id="obligation-note" label="Note">
            <Textarea id="obligation-note" value={form.note} maxLength={2000} rows={2} onChange={(e) => set("note")(e.target.value)} />
          </Row>
        </div>
        <Footer req={req} onClose={onClose} onSubmit={submit} disabled={!valid} label={obligation ? "Enregistrer" : "Créer l'obligation"} testid="obligation-submit" />
      </DialogContent>
    </Dialog>
  );
}

export function DoneDialog({ obligation, open, onClose, onDone }) {
  const req = useSubmit();
  const [day, setDay] = useState(todayIso());
  const [note, setNote] = useState("");
  useEffect(() => { if (open) { setDay(todayIso()); setNote(""); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!obligation) return null;
  const repeats = obligation.recurrence !== "none";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Marquer « {obligation.title} » faite</DialogTitle>
          <DialogDescription>{repeats ? `Récurrence ${RECURRENCES[obligation.recurrence].toLowerCase()} : la suivante sera créée aussitôt.` : "L'obligation sort de la liste à faire."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Row id="obligation-done-on" label="Faite le">
            <Input id="obligation-done-on" type="date" max={todayIso()} value={day} onChange={(e) => setDay(e.target.value)} className="h-10" data-testid="obligation-done-on" />
          </Row>
          <Row id="obligation-done-note" label="Note">
            <Textarea id="obligation-done-note" value={note} maxLength={2000} rows={2} onChange={(e) => setNote(e.target.value)} placeholder="Ex. déposée au guichet, reçu n°…" />
          </Row>
        </div>
        <Footer req={req} onClose={onClose} disabled={!day} label="Marquer faite" testid="obligation-done-submit"
          onSubmit={() => req.run(() => api.post(`/records/obligations/${obligation.id}/done`, { expected_version: obligation.version, done_on: day, note: note.trim() }),
            onDone, repeats ? "Obligation faite ; la suivante est créée." : "Obligation faite.")} />
      </DialogContent>
    </Dialog>
  );
}

export function CancelDialog({ obligation, open, onClose, onDone }) {
  const req = useSubmit();
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) { setReason(""); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!obligation) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Annuler « {obligation.title} »</DialogTitle>
          <DialogDescription>Elle reste visible dans « Annulées », avec son motif. Aucune alerte ne partira plus.</DialogDescription>
        </DialogHeader>
        <Row id="obligation-cancel-reason" label="Motif">
          <Textarea id="obligation-cancel-reason" value={reason} maxLength={1000} rows={3} onChange={(e) => setReason(e.target.value)} data-testid="obligation-cancel-reason" />
        </Row>
        <Footer req={req} onClose={onClose} danger disabled={reason.trim().length < 3} label="Annuler l'obligation" testid="obligation-cancel-submit"
          onSubmit={() => req.run(() => api.post(`/records/obligations/${obligation.id}/cancel`, { expected_version: obligation.version, reason: reason.trim() }),
            onDone, "Obligation annulée.")} />
      </DialogContent>
    </Dialog>
  );
}

// D-42: « Créer une tâche » opens the usual task form (legal & admin domain, due date prefilled); the created task is then linked.
export function useObligationTask(onLinked) {
  const { user } = useAuth();
  const [target, setTarget] = useState(null);
  const canCreateTask = hasPermission(user, "tasks:create");
  const sheet = (
    <TaskFormSheet open={!!target} onOpenChange={(v) => { if (!v) setTarget(null); }} task={null} canScopeAll={hasPermission(user, "tasks:scope_all")}
      canEdit={false} directory={[]}
      initialValues={target ? { title: target.title, due_date: target.due_date, domain: "legal_admin", priority: target.state === "overdue" ? "high" : "medium",
        description: `Obligation : ${target.title}${target.document ? ` (document ${target.document.number})` : ""}` } : undefined}
      onSaved={async (task) => {
        const obligation = target;
        setTarget(null);
        try {
          const { data } = await api.post(`/records/obligations/${obligation.id}/tasks`, { task_id: task.id });
          toast.success("Tâche créée et reliée à l'obligation.");
          onLinked?.(data);
        } catch (e) {
          toast.error(recordsError(e, "Tâche créée, mais pas reliée à l'obligation."));
        }
      }} />
  );
  return { open: canCreateTask ? setTarget : null, sheet };
}

export function ObligationActions({ obligation, onEdit, onDone, onCancel, onTask, onDelete }) {
  const caps = obligation.capabilities || {};
  const items = [
    caps.close && ["done", "Marquer faite", onDone],
    caps.link_task && onTask && ["task", "Créer une tâche", onTask],
    caps.edit && ["edit", "Modifier", onEdit],
    caps.close && ["cancel", "Annuler", onCancel],
    caps.delete && onDelete && ["delete", "Supprimer…", onDelete],
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Actions sur ${obligation.title}`} data-testid={`obligation-more-${obligation.id}`}><MoreHorizontal size={16} /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" title={obligation.title}>
        {items.map(([id, label, handler]) => (
          <DropdownMenuItem key={id} onClick={() => handler(obligation)} data-testid={`obligation-action-${id}`}
            className={id === "cancel" || id === "delete" ? "text-red-600" : ""}>{label}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ObligationLine({ obligation, children, showDocument = true }) {
  return (
    <div className="flex items-start gap-3 py-2.5" data-testid={`obligation-${obligation.id}`}>
      <CalendarClock size={16} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-900 break-words">{obligation.title}</span>
          <ObligationBadge state={obligation.state} />
          {obligation.recurrence !== "none" && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Repeat size={12} />{RECURRENCES[obligation.recurrence]}</span>}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
          <span>{OBLIGATION_KINDS[obligation.kind]}</span>
          <span className="tabular-nums">{obligation.due_date.split("-").reverse().join("/")} · {dueText(obligation)}</span>
          <span>{obligation.responsible_name}</span>
          {showDocument && obligation.document && (
            <Link to={`/documents/${obligation.document.id}`} className="text-jamora hover:underline">{obligation.document.number}</Link>
          )}
        </div>
        {obligation.cancel_reason && <p className="text-xs text-slate-500">Motif : {obligation.cancel_reason}</p>}
        {obligation.done_note && <p className="text-xs text-slate-500">{obligation.done_note}</p>}
      </div>
      {children}
    </div>
  );
}

// The document page's « Obligations » card: its obligations and, from its dates, the ones it suggests (D-40) — created on a click only.
export function DocumentObligationsCard({ doc }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [dialog, setDialog] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/records/obligations", { params: { document_id: doc.id, limit: 50 } });
      setItems(data.items);
    } catch {
      setItems([]);
    }
  };
  useEffect(() => { load(); }, [doc.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const task = useObligationTask(load);
  if (items === null) return null;
  const canCreate = canWrite(user, "records:create") && !["archived", "abandoned"].includes(doc.state);
  const suggestions = canCreate ? obligationSuggestions(doc, items) : [];
  const done = () => { setDialog(null); load(); };
  return (
    <Card className="p-4 sm:p-5 space-y-2" data-testid="records-obligations-card">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold text-slate-900">Obligations</h2>
        {canCreate && <Button variant="outline" size="sm" onClick={() => setDialog({ kind: "create" })} data-testid="records-obligation-new"><Plus size={14} className="mr-1" />Ajouter</Button>}
      </div>
      {suggestions.map((s) => (
        <div key={s.kind} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid={`records-obligation-suggestion-${s.kind}`}>
          <span>{s.kind === "notice" ? "Préavis" : "Fin de contrat"} le {s.due_date.split("-").reverse().join("/")} : aucune obligation ne le suit.</span>
          <Button size="sm" variant="outline" onClick={() => setDialog({ kind: "create", preset: { title: s.title, kind: s.kind, due_date: s.due_date } })}
            data-testid={`records-obligation-suggest-${s.kind}`}>Créer l'obligation</Button>
        </div>
      ))}
      {items.length === 0 && suggestions.length === 0 && <p className="text-sm text-slate-500">Aucune obligation liée à ce document.</p>}
      <div className="divide-y divide-slate-100">
        {items.map((o) => (
          <ObligationLine key={o.id} obligation={o} showDocument={false}>
            <ObligationActions obligation={o} onEdit={(ob) => setDialog({ kind: "edit", obligation: ob })} onDone={(ob) => setDialog({ kind: "done", obligation: ob })}
              onCancel={(ob) => setDialog({ kind: "cancel", obligation: ob })} onTask={task.open} />
          </ObligationLine>
        ))}
      </div>
      {items.length > 0 && (
        <Link to="/documents/obligations" className="inline-flex items-center gap-1 text-sm text-jamora hover:underline">Toutes les obligations<ChevronRight size={14} /></Link>
      )}
      <ObligationDialog open={dialog?.kind === "create" || dialog?.kind === "edit"} onClose={() => setDialog(null)} onDone={done}
        obligation={dialog?.kind === "edit" ? dialog.obligation : null} preset={dialog?.preset} document={doc} />
      <DoneDialog obligation={dialog?.obligation} open={dialog?.kind === "done"} onClose={() => setDialog(null)} onDone={done} />
      <CancelDialog obligation={dialog?.obligation} open={dialog?.kind === "cancel"} onClose={() => setDialog(null)} onDone={done} />
      {task.sheet}
    </Card>
  );
}
